import React, { useState } from 'react';
import { Progress } from 'semantic-ui-react';
import { Moment } from 'moment-timezone';

import PrimaryButton from '@modules/common/components/buttons/PrimaryButton';
import { alertError, alertSuccess } from '@src/lib/alert';
import SentryUtils from '@src/lib/sentry';
import BaseModal from '@modules/common/components/BaseModal';
import { SpacedBetween } from '@src/components/common/SpacedStyle';
import { styled } from '@modules/common/components/ThemeProvider/utils';
import config from '@config/config';

import {
  OAuthFlow,
  NewOAuthClientDescriptorPayload,
  OAuthClientDescriptor,
} from '../../../components/types/oauth-client/index';
import ClientResource from '../../../components/services/ClientResource';
import ClientCreated from './ClientCreated';
import NewClientForm from './NewClientForm';
import ClientLimitReachedMessage from './ClientLimitExceededMessage';
import { getDefaultExpiryDate } from '../ClientForm/utils';

type Props = {
  activeClientsCount: number;
  onClientCreated: () => void;
  ownerId: string;
  limit: number | undefined;
};

function NewClient({ ownerId, limit, activeClientsCount, onClientCreated }: Props) {
  const [creatingClient, setCreatingClient] = useState<boolean>(false);
  const [createdClient, setCreatedClient] = useState<OAuthClientDescriptor>();

  const hasLimit = limit !== undefined;
  const limitReached = limit !== undefined && activeClientsCount >= limit;

  async function createClient(
    selectedOAuthFlow: OAuthFlow,
    clientName: string,
    allowedOriginDomains: string[] | undefined,
    isSPAEnabled: boolean,
    expirationDisabled: boolean,
    expiryDate?: Moment,
  ) {
    if (!selectedOAuthFlow) {
      throw new Error('Invalid flow');
    }
    const clientDescriptor: NewOAuthClientDescriptorPayload = {
      name: clientName,
      accountId: ownerId,
      isSPAEnabled,
      parameters: {
        supportedFlows: [selectedOAuthFlow],
        allowedWebOrigins: allowedOriginDomains,
      },
    };
    if (config.oAuthClientsExpiryEnabled) {
      if (expiryDate) {
        clientDescriptor.expiryDate = expiryDate;
      } else if (!expirationDisabled) {
        clientDescriptor.expiryDate = getDefaultExpiryDate();
      }
    }
    try {
      setCreatingClient(true);
      const response: OAuthClientDescriptor = await ClientResource.create(clientDescriptor);
      setCreatedClient(response);
      alertSuccess('Client successfully created');
      onClientCreated();
    } catch (err) {
      let errorMessage: string;
      if (err.status === 403 && err.body.error.code === 'OAUTH_CLIENT_LIMIT_REACHED') {
        errorMessage = err.body.error.message;
      } else {
        errorMessage = 'Something went wrong while creating OAuth client';
        SentryUtils.captureAPIError(err, errorMessage);
      }
      alertError(errorMessage);
    }
    setCreatingClient(false);
  }

  function trigger(handleOpen: () => void) {
    return (
      <>
        {!limitReached && (
          <SpacedBetween>
            <PrimaryButton
              type="submit"
              icon="add"
              onClick={handleOpen}
              content="Create"
              style={{ marginBottom: '5px' }}
            />
            {hasLimit && (
              <StyledProgress
                value={activeClientsCount}
                total={limit}
                progress="ratio"
                label="active clients created"
                noClientsCreated={activeClientsCount === 0}
              />
            )}
          </SpacedBetween>
        )}
        {limitReached && (
          <ClientLimitReachedMessage limit={limit} activeClientsCount={activeClientsCount} />
        )}
      </>
    );
  }

  const shouldDisplayClientCreated = !creatingClient && createdClient;
  return (
    <BaseModal
      trigger={trigger}
      closeOnDimmerClick={!shouldDisplayClientCreated}
      closeOnEscape={!shouldDisplayClientCreated}
    >
      {handleClose =>
        shouldDisplayClientCreated ? (
          <ClientCreated
            client={createdClient!}
            handleClose={() => {
              setCreatedClient(undefined);
              handleClose();
            }}
          />
        ) : (
          <NewClientForm
            handleClose={handleClose}
            createClient={createClient}
            creatingClient={creatingClient}
          />
        )
      }
    </BaseModal>
  );
}

export default NewClient;

const StyledProgress = styled(Progress)<{ noClientsCreated: boolean }>`
  width: 30%;
  max-width: 200px !important;
  border-radius: 0 !important;

  .bar {
    border-radius: 0 !important;
    background: ${props => (props.noClientsCreated ? 'unset' : '#888')} !important;
    min-width: 0 !important;
  }

  .bar .progress {
    left: 4px !important;
    color: white !important;
  }
`;
